import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';

const axios = require('axios').default;

export default function useHouseholds({ team, contact, csrfToken }) {
  const [households, setHouseholds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createSubmitting, setCreateSubmitting] = useState(false);
  const [createErrors, setCreateErrors] = useState([]);

  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [addMemberHousehold, setAddMemberHousehold] = useState(null);
  const [memberSearch, setMemberSearch] = useState('');
  const [memberResults, setMemberResults] = useState([]);
  const [memberSearchLoading, setMemberSearchLoading] = useState(false);
  const [selectedFanId, setSelectedFanId] = useState(null);
  const [addMemberSubmitting, setAddMemberSubmitting] = useState(false);
  const [addMemberError, setAddMemberError] = useState('');

  const [memberToRemove, setMemberToRemove] = useState(null);
  const [memberToRemoveHousehold, setMemberToRemoveHousehold] = useState(null);
  const [removeMemberSubmitting, setRemoveMemberSubmitting] = useState(false);

  const [householdToDelete, setHouseholdToDelete] = useState(null);
  const [deleteHouseholdSubmitting, setDeleteHouseholdSubmitting] = useState(false);

  const fetchHouseholds = async () => {
    if (!team || !contact || !contact.id) return;

    try {
      setLoading(true);
      setError('');
      if (csrfToken) {
        axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
      }

      const response = await axios.get(
        `/teams/${team.id}/crm/fans/${contact.id}/households.json`,
      );

      const data = response.data || [];
      setHouseholds(data);
    } catch (err) {
      setError('Failed to load households');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHouseholds();
  }, [team, contact, csrfToken]);

  const fetchHouseholdDetail = async (householdId) => {
    try {
      if (csrfToken) {
        axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
      }
      const response = await axios.get(
        `/teams/${team.id}/crm/households/${householdId}.json`,
      );
      return response.data;
    } catch (err) {
      return null;
    }
  };

  const refreshHousehold = async (householdId) => {
    const detail = await fetchHouseholdDetail(householdId);
    if (detail) setHouseholds((prev) => prev.map((h) => (h.id === detail.id ? detail : h)));
    return !!detail;
  };

  const addGuest = async (household, attributes) => {
    try {
      axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
      await axios.post(
        `/teams/${team.id}/crm/households/${household.id}/household_guests.json`,
        { household_guest: attributes },
      );
    } catch (err) {
      return Array.isArray(err.response?.data) ? err.response.data : ['Failed to add guest'];
    }
    if (!(await refreshHousehold(household.id))) toast.error("Guest saved, but the list didn't refresh. Reload the page to see it.");
    return [];
  };

  const removeGuest = async (household, guest) => {
    try {
      axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
      await axios.delete(`/teams/${team.id}/crm/households/${household.id}/household_guests/${guest.id}.json`);
    } catch (err) {
      toast.error('Failed to remove guest');
      return;
    }
    if (!(await refreshHousehold(household.id))) toast.error("Guest removed, but the list didn't refresh. Reload the page to see it.");
  };

  const openCreateModal = () => {
    setCreateName('');
    setCreateErrors([]);
    setCreateSubmitting(false);
    setShowCreateModal(true);
  };

  const closeCreateModal = () => {
    if (createSubmitting) return;
    setShowCreateModal(false);
  };

  const canSubmitCreate = !createSubmitting;

  const defaultHouseholdName = () => {
    const lastName = contact && contact.last_name ? contact.last_name.trim() : '';
    if (lastName) return `${lastName} Household`;
    return null;
  };

  const handleCreateHousehold = async (e) => {
    e.preventDefault();
    if (createSubmitting) return;
    if (!team || !team.id || !contact || !contact.id || !csrfToken) return;

    try {
      setCreateSubmitting(true);
      setCreateErrors([]);
      axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;

      const response = await axios.post(
        `/teams/${team.id}/crm/fans/${contact.id}/households.json`,
        {
          household: {
            name: createName.trim() || defaultHouseholdName(),
            household_memberships_attributes: [
              { fan_id: contact.id, level: 'primary' },
            ],
          },
        },
      );

      const created = response.data;
      setHouseholds((prev) => [...prev, created]);
      setShowCreateModal(false);
    } catch (err) {
      const resp = err.response;
      if (resp && Array.isArray(resp.data)) {
        setCreateErrors(resp.data);
      } else {
        setCreateErrors(['Failed to create household']);
      }
    } finally {
      setCreateSubmitting(false);
    }
  };

  const openAddMemberModal = (household) => {
    setAddMemberHousehold(household);
    setMemberSearch('');
    setMemberResults([]);
    setSelectedFanId(null);
    setAddMemberError('');
    setAddMemberSubmitting(false);
    setMemberSearchLoading(false);
    setShowAddMemberModal(true);
  };

  const closeAddMemberModal = () => {
    if (addMemberSubmitting) return;
    setShowAddMemberModal(false);
    setAddMemberHousehold(null);
  };

  const handleSearchFans = async (options = {}) => {
    const shouldApply = options.shouldApply || (() => true);
    if (!team || !team.id) return;

    const query = memberSearch.trim();
    if (!query) {
      setMemberResults([]);
      setSelectedFanId(null);
      return;
    }

    try {
      setMemberSearchLoading(true);

      const existingFanIds = (
        addMemberHousehold && addMemberHousehold.household_memberships
          ? addMemberHousehold.household_memberships
          : []
      )
        .map((m) => m.fan && m.fan.id)
        .filter(Boolean);

      const response = await axios.get(
        `/teams/${team.id}/crm/fans/autocomplete`,
        {
          params: {
            name: query,
            omit_ids: existingFanIds,
          },
        },
      );

      const results = response.data || [];
      if (!shouldApply()) return;
      setMemberResults(results);
      if (!results.find((f) => f.id === selectedFanId)) {
        setSelectedFanId(null);
      }
    } catch (err) {
      if (shouldApply()) setMemberResults([]);
    } finally {
      setMemberSearchLoading(false);
    }
  };

  useEffect(() => {
    if (!showAddMemberModal) return undefined;

    if (!memberSearch.trim()) {
      setMemberResults([]);
      setSelectedFanId(null);
      return undefined;
    }

    let ignore = false;
    handleSearchFans({ shouldApply: () => !ignore });
    return () => {
      ignore = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberSearch, showAddMemberModal, addMemberHousehold]);

  const handleAddMember = async (e) => {
    e.preventDefault();
    if (!team || !team.id || !csrfToken || !addMemberHousehold || !selectedFanId) return;

    try {
      setAddMemberSubmitting(true);
      setAddMemberError('');
      axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;

      await axios.post(
        `/teams/${team.id}/crm/households/${addMemberHousehold.id}/household_memberships.json`,
        { household_membership: { fan_id: selectedFanId, level: 'secondary' } },
      );

      await refreshHousehold(addMemberHousehold.id);

      setShowAddMemberModal(false);
      setAddMemberHousehold(null);
    } catch (err) {
      const resp = err.response;
      if (resp && resp.data && Array.isArray(resp.data)) {
        setAddMemberError(resp.data.join(', '));
      } else {
        setAddMemberError('Failed to add member');
      }
    } finally {
      setAddMemberSubmitting(false);
    }
  };

  const promptRemoveMember = (household, membership) => {
    setMemberToRemoveHousehold(household);
    setMemberToRemove(membership);
    setRemoveMemberSubmitting(false);
  };

  const closeRemoveMemberModal = () => {
    if (removeMemberSubmitting) return;
    setMemberToRemove(null);
    setMemberToRemoveHousehold(null);
  };

  const confirmRemoveMember = async () => {
    if (!team || !team.id || !csrfToken || !memberToRemoveHousehold || !memberToRemove) return;

    try {
      setRemoveMemberSubmitting(true);
      axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
      await axios.delete(
        `/teams/${team.id}/crm/households/${memberToRemoveHousehold.id}/household_memberships/${memberToRemove.id}.json`,
      );

      await refreshHousehold(memberToRemoveHousehold.id);
      setMemberToRemove(null);
      setMemberToRemoveHousehold(null);
    } catch (err) {
      // eslint-disable-next-line no-alert
      alert('Failed to remove member');
    } finally {
      setRemoveMemberSubmitting(false);
    }
  };

  const promptDeleteHousehold = (household) => {
    setHouseholdToDelete(household);
    setDeleteHouseholdSubmitting(false);
  };

  const closeDeleteHouseholdModal = () => {
    if (deleteHouseholdSubmitting) return;
    setHouseholdToDelete(null);
  };

  const confirmDeleteHousehold = async () => {
    if (!team || !team.id || !csrfToken || !householdToDelete) return;

    try {
      setDeleteHouseholdSubmitting(true);
      axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
      await axios.delete(
        `/teams/${team.id}/crm/households/${householdToDelete.id}.json`,
      );
      setHouseholds((prev) => prev.filter((h) => h.id !== householdToDelete.id));
      setHouseholdToDelete(null);
    } catch (err) {
      const reason = Array.isArray(err.response?.data) ? err.response.data.join(' ') : '';
      // eslint-disable-next-line no-alert
      alert(reason || 'Failed to delete household');
    } finally {
      setDeleteHouseholdSubmitting(false);
    }
  };

  const updateMembership = async (household, membership, attributes, failureMessage) => {
    if (!team || !team.id || !csrfToken) return;

    try {
      axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
      await axios.patch(
        `/teams/${team.id}/crm/households/${household.id}/household_memberships/${membership.id}.json`,
        { household_membership: attributes },
      );

      await refreshHousehold(household.id);
    } catch (err) {
      toast.error(failureMessage);
    }
  };

  const handleChangeMemberLevel = (household, membership, newLevel) =>
    updateMembership(household, membership, { level: newLevel }, 'Failed to update member level');

  const handleChangeMemberRelationship = (household, membership, newRelationship) =>
    updateMembership(household, membership, { relationship: newRelationship }, 'Failed to update relationship');

  return {
    households,
    loading,
    error,
    showCreateModal,
    createName,
    setCreateName,
    createSubmitting,
    createErrors,
    canSubmitCreate,
    openCreateModal,
    closeCreateModal,
    handleCreateHousehold,
    showAddMemberModal,
    addMemberHousehold,
    memberSearch,
    setMemberSearch,
    memberResults,
    memberSearchLoading,
    selectedFanId,
    setSelectedFanId,
    addMemberSubmitting,
    addMemberError,
    openAddMemberModal,
    closeAddMemberModal,
    handleSearchFans,
    handleAddMember,
    promptRemoveMember,
    memberToRemove,
    memberToRemoveHousehold,
    removeMemberSubmitting,
    closeRemoveMemberModal,
    confirmRemoveMember,
    promptDeleteHousehold,
    householdToDelete,
    deleteHouseholdSubmitting,
    closeDeleteHouseholdModal,
    confirmDeleteHousehold,
    handleChangeMemberLevel,
    handleChangeMemberRelationship,
    addGuest,
    removeGuest,
    fetchHouseholds,
  };
}
